import React, { useCallback, useState } from 'react'
import { View, ScrollView } from 'react-native'
import {
  Button,
  Modal,
  Portal,
  Text,
  ActivityIndicator,
  List,
  Divider,
  IconButton,
} from 'react-native-paper'
import { Row } from '../../../../@ui'
import { useTranslation } from 'react-i18next'
import {
  useMyCollaboratorListsQuery,
  useDeleteCollaboratorListMutation,
  MyCollaboratorListsQuery,
} from '../../../../generated/graphql'
import Toast from 'react-native-root-toast'
import * as Sentry from '@sentry/react-native'
import { createStyle } from '../../../../contexts/global-styles.provider'

type CollaboratorList = NonNullable<
  NonNullable<MyCollaboratorListsQuery['myCollaboratorLists']>[number]
>

type ViewCollaboratorListsModalProps = {
  visible: boolean
  onDismiss: () => void
  onOpenCreateList?: () => void
}

export const ViewCollaboratorListsModal: React.FC<
  ViewCollaboratorListsModalProps
> = ({ visible, onDismiss, onOpenCreateList }) => {
  const { t } = useTranslation('nodes')
  const styles = useStyle()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data, loading, refetch } = useMyCollaboratorListsQuery({
    skip: !visible,
  })

  const [deleteList] = useDeleteCollaboratorListMutation()

  const lists = data?.myCollaboratorLists || []

  const handleDelete = useCallback(
    async (listId: string) => {
      try {
        setDeletingId(listId)
        await deleteList({
          variables: { listId },
        })
        Toast.show(t('list-deleted-successfully'))
        await refetch()
      } catch (e) {
        Toast.show((e as Error).message)
        console.error(e)
        Sentry.captureException(e)
      } finally {
        setDeletingId(null)
      }
    },
    [deleteList, refetch, t]
  )

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modalContainer}
      >
        <View style={styles.modalContent}>
          <Row
            alignItems="center"
            justifyContent="space-between"
            style={styles.titleRow}
          >
            <Text variant="titleLarge" style={styles.modalTitle}>
              {t('tasks:my-lists')}
            </Text>
            {onOpenCreateList ? (
              <IconButton
                icon="plus"
                mode="contained"
                onPress={onOpenCreateList}
                size={22}
                style={styles.addListButton}
                accessibilityLabel={t('create-list')}
              />
            ) : null}
          </Row>

          {loading && lists.length === 0 ? (
            <ActivityIndicator style={styles.loader} />
          ) : lists.length === 0 ? (
            <Text style={styles.emptyText}>{t('no-lists-found')}</Text>
          ) : (
            <ScrollView
              style={styles.list}
              contentContainerStyle={styles.listContent}
            >
              {lists.map((list, index) => {
                const isDeleting = deletingId === list.id

                return (
                  <View key={list.id}>
                    <View style={styles.listItem}>
                      <List.Item
                        title={list.name}
                        titleStyle={styles.listItemTitle}
                        left={() => (
                          <View style={styles.colorIndicatorWrapper}>
                            {list.color ? (
                              <View
                                style={[
                                  styles.colorIndicator,
                                  { backgroundColor: list.color },
                                ]}
                              />
                            ) : null}
                          </View>
                        )}
                        right={() => {
                          if (isDeleting) {
                            return (
                              <ActivityIndicator
                                size="small"
                                style={styles.itemLoader}
                              />
                            )
                          }
                          return (
                            <IconButton
                              icon="delete-outline"
                              size={22}
                              iconColor="red"
                              onPress={() => handleDelete(list.id)}
                              style={styles.deleteButton}
                              accessibilityLabel={t('delete')}
                            />
                          )
                        }}
                      />
                    </View>
                    {index < lists.length - 1 && (
                      <Divider style={styles.separator} />
                    )}
                  </View>
                )
              })}
            </ScrollView>
          )}

          <Button onPress={onDismiss} style={styles.closeButton}>
            {t('common:close')}
          </Button>
        </View>
      </Modal>
    </Portal>
  )
}

const useStyle = createStyle(({ theme, isMobile }) => ({
  modalContainer: {
    width: isMobile ? '100%' : '60%',
    alignSelf: 'center',
    backgroundColor: theme.colors.surface,
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    borderRadius: 8,
    maxHeight: '80%',
  },
  modalContent: {
    flex: 1,
    minHeight: 200,
  },
  titleRow: {
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  modalTitle: {
    flex: 1,
  },
  addListButton: {
    margin: 0,
  },
  list: {
    flex: 1,
    maxHeight: 400,
  },
  emptyText: {
    textAlign: 'center',
    padding: theme.spacing(4),
    opacity: 0.6,
  },
  listContent: {
    paddingBottom: theme.spacing(1),
  },
  separator: {
    marginVertical: theme.spacing(0.5),
  },
  listItem: {
    paddingVertical: theme.spacing(0.5),
  },
  listItemTitle: {
    fontSize: 16,
    fontWeight: '500',
  },
  colorIndicatorWrapper: {
    width: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  colorIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
  },
  deleteButton: {
    margin: 0,
  },
  itemLoader: {
    marginRight: theme.spacing(2),
  },
  loader: {
    marginVertical: theme.spacing(4),
  },
  closeButton: {
    marginTop: theme.spacing(2),
  },
}))
